import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Clock } from "lucide-react";
import Layout from "../layout/Layout";
import BlogContent from "../Components/blog/BlogContent";
import { getPostBySlug, formatBlogDate } from "../data/blogPosts";
import { SITE_URL } from "../data/siteData";

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  if (!post) {
    return (
      <Layout>
        <div className="mx-auto max-w-3xl px-6 py-24 text-center">
          <h1 className="mb-4 font-display text-3xl font-bold text-brand-ink">Article not found</h1>
          <Link to="/blog" className="font-medium text-brand-purple hover:underline">
            Back to blog
          </Link>
        </div>
      </Layout>
    );
  }

  const url = `${SITE_URL}/blog/${post.slug}`;
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    datePublished: post.date,
    author: {
      "@type": "Person",
      name: post.author,
      jobTitle: post.authorRole,
    },
    publisher: {
      "@type": "Organization",
      name: "Quantum Spark Technology",
      logo: {
        "@type": "ImageObject",
        url: `${SITE_URL}/Artboardcopy1.png`,
      },
    },
    mainEntityOfPage: url,
  };

  return (
    <Layout>
      <Helmet>
        <title>{post.title} | Quantum Spark Technology Blog</title>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={url} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      <article className="mx-auto max-w-3xl px-6 py-16 md:py-24">
        <Link to="/blog" className="mb-8 inline-flex items-center text-sm font-medium text-brand-purple hover:underline">
          <ArrowLeft size={16} className="mr-2" />
          Back to blog
        </Link>

        <p className="mb-4 text-sm font-semibold uppercase tracking-widest text-brand-purple">{post.category}</p>
        <h1 className="mb-6 font-display text-4xl font-bold leading-tight text-brand-ink md:text-5xl">{post.title}</h1>

        <div className="mb-10 flex flex-wrap items-center gap-4 border-b border-zinc-200 pb-8 text-sm text-brand-muted">
          <span>
            By <strong className="text-brand-ink">{post.author}</strong>, {post.authorRole}
          </span>
          <span>{formatBlogDate(post.date)}</span>
          <span className="flex items-center gap-1">
            <Clock size={14} />
            {post.readTime}
          </span>
        </div>

        <BlogContent sections={post.sections} />

        <div className="mt-12 rounded-2xl border border-zinc-200 bg-[#FAFAFB] p-8">
          <h2 className="mb-2 font-display text-xl font-bold text-brand-ink">Planning a project?</h2>
          <p className="mb-4 text-brand-muted">
            Quantum Spark Technology helps founders ship web apps, mobile products, and AI features with senior-led
            delivery. Tell us what you are building — we respond within 24 hours.
          </p>
          <Link to="/contact" className="font-semibold text-brand-purple hover:underline">
            Contact our team →
          </Link>
        </div>
      </article>
    </Layout>
  );
}
